import { Item, logEvent as logFirebaseEvent } from '@firebase/analytics';
import {
  analyticsPromise,
  FirebaseCallable,
  functions,
  httpsCallable,
} from '@util/firebase';
import { logError } from '@util/logError';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import { ProductDocument, ProductLight } from 'models/product';
import { EventTypes, PixelEvents } from './analytics.types';
dayjs.extend(tz);

const isProd = process.env.NODE_ENV === 'production';

const pixelTrack = (
  eventName: PixelEvents,
  eventParams: Record<string, any>,
  uid?: string | null,
  order_id?: string | null
) => {
  try {
    if (typeof window === 'undefined' || !window.fbq) return;
    const getPixelEventID = ({
      eventName, // ViewContent, AddToCart, etc.
      uid,
      content_ids = [],
    }: {
      eventName: PixelEvents;
      uid?: string | null;
      content_ids?: string[];
    }): { eventID: string } | undefined => {
      if (order_id && eventName === 'Purchase') return { eventID: order_id };
      if (!uid) return undefined; // no eventID needed for backend dedupe
      const isoDate = new Date().toISOString();
      const minuteBucket = isoDate.substring(0, isoDate.lastIndexOf(':'));
      const ids = content_ids.sort().join('-');
      const eventID = `${eventName}-${ids}-${uid}-${minuteBucket}`;
      return { eventID };
    };
    const eventId = getPixelEventID({
      eventName,
      uid,
      content_ids: eventParams.content_ids,
    });
    window.fbq('track', eventName, eventParams, eventId);
  } catch (e) {
    logError(e, eventName);
  }
};

// all GA4 events not listed below
export async function logEvent(
  eventName: Exclude<
    EventTypes,
    'add_to_cart' | 'view_item' | 'begin_checkout' | 'sign_up'
  >,
  eventParams: {
    coupon?: string | undefined;
    currency?: string | undefined;
    items?: Item[] | undefined;
    payment_type?: string | undefined;
    value?: number | undefined;

    [key: string]: any;
  },
  uid?: string,
  order_id?: string | null
) {
  if (!isProd) return;
  try {
    if (!eventParams) throw new Error('Event params are required');
    // GA events
    const analytics = await analyticsPromise();
    if (analytics)
      logFirebaseEvent(analytics, eventName as string, eventParams);
    // fb pixel events
    if (eventName === 'add_payment_info')
      addPaymentInfo(eventParams as { id: string; is_payout: boolean }, uid);

    if (eventName === 'purchase' && eventParams.items?.length)
      logPurchase(eventParams, uid, order_id);
    if (eventName === 'add_to_wishlist' && eventParams.items?.length)
      addToWishlist(eventParams, uid);
    if (eventName === 'created_offer' && eventParams.items?.length)
      logOffer(eventParams.items![0], uid);
  } catch (e) {
    logError(e, eventName);
  }
}

// Main analytics functions with extra config

export async function viewContent(item: ProductDocument) {
  if (!isProd) return null;
  let uid: string | null = null;
  try {
    const { data } = await httpsCallable<
      { item: ProductDocument; total: number; from_web: boolean },
      { uid: string | null }
    >(
      functions,
      FirebaseCallable.viewedProduct
    )({
      item,
      total: item.price,
      from_web: true,
    });
    uid = data?.uid;
    const analytics = await analyticsPromise();
    if (analytics) {
      logFirebaseEvent(analytics, 'view_item', {
        currency: 'usd',
        value: item.price,
        items: [getItem(item)],
      });
      logFirebaseEvent(analytics, 'category_product_view', {
        category: item.category,
        subcategory: item.category1,
        item_id: item.id,
        value: item.price,
        items: [getItem(item)],
      });
    }
  } catch (e) {
    logError(e);
  } finally {
    pixelTrack(
      'ViewContent',
      {
        content_ids: [item.id],
        content_name: item.title,
        content_category: item.category,
        content_type: 'product',
        contents: [{ id: item.id, quantity: 1 }],
        value: item.price,
        currency: 'USD',
      },
      uid
    );
  }
}

export async function logInitCheckout(
  items: ProductDocument[],
  total: number,
  uid: string
) {
  if (!isProd) return;
  try {
    await httpsCallable(
      functions,
      FirebaseCallable.initiateCheckout
    )({
      items,
      total,
      isComplete: false,
      from_web: true,
    });
    const analytics = await analyticsPromise();
    if (analytics)
      logFirebaseEvent(analytics, 'begin_checkout', {
        currency: 'usd',
        value: total,
        items: getItems(items),
      });
  } catch (e) {
    logError(e);
  } finally {
    pixelTrack(
      'InitiateCheckout',
      {
        content_ids: items.map((i) => i.id),
        contents: items.map((i) => {
          return { id: i.id, quantity: 1 };
        }),
        num_items: items.length,
        value: total,
        currency: 'USD',
      },
      uid
    );
  }
}

export async function logAddToCart(
  item: ProductDocument,
  total: number,
  uid: string
) {
  if (!isProd) return;
  try {
    await httpsCallable<
      { item: ProductDocument; total: number; from_web: boolean },
      void
    >(
      functions,
      FirebaseCallable.addToCart
    )({ item, total, from_web: true });
    const analytics = await analyticsPromise();
    if (analytics)
      logFirebaseEvent(analytics, 'add_to_cart', {
        currency: 'usd',
        value: item.price,
        items: [getItem(item)],
      });
  } catch (e) {
    logError(e);
  } finally {
    pixelTrack(
      'AddToCart',
      {
        content_ids: [item.id],
        content_name: item.title,
        content_type: 'product',
        contents: [{ id: item.id, quantity: 1 }],
        value: total,
        currency: 'USD',
      },
      uid
    );
  }
}

export async function completeRegistration(
  uid: string,
  method: string,
  email: string
) {
  try {
    await httpsCallable<
      { email: string; from_web: boolean; timezone: string },
      void
    >(
      functions,
      FirebaseCallable.completeRegistration
    )({ email, from_web: true, timezone: dayjs.tz.guess() });
    const analytics = await analyticsPromise();
    if (analytics) logFirebaseEvent(analytics, 'sign_up', { method });
  } catch (e) {
    logError(e);
  } finally {
    pixelTrack(
      'CompleteRegistration',
      {
        status: true,
        content_name: method,
      },
      uid
    );
  }
}

// helper fb pixel functions

function logPurchase(
  eventParams: { items?: Item[]; value?: number },
  uid?: string,
  order_id?: string | null
) {
  const content_ids: string[] = [];
  eventParams.items?.forEach((i) => {
    if (i.item_id) content_ids.push(i.item_id);
  });
  pixelTrack(
    'Purchase',
    {
      value: eventParams.value ?? 0,
      currency: 'USD',
      ...(content_ids.length && { content_ids }),
    },
    uid,
    order_id
  );
}

function addPaymentInfo(
  eventParams: { id: string; is_payout: boolean },
  uid?: string
) {
  pixelTrack(
    'AddPaymentInfo',
    {
      content_ids: [eventParams.id],
      content_category: eventParams.is_payout ? 'For Payout' : 'For Purchase',
    },
    uid
  );
}

function addToWishlist(
  eventParams: {
    currency?: string;
    items?: Item[];
    value?: number;
  },
  uid?: string
) {
  pixelTrack(
    'AddToWishlist',
    {
      content_ids: [eventParams.items![0].item_id!],
      content_name: eventParams.items![0].item_name,
      value: eventParams.value,
      currency: eventParams.currency!,
    },
    uid
  );
}

function logOffer(item: Item, uid?: string) {
  pixelTrack(
    'Lead',
    {
      content_ids: [item.item_id],
      content_category: item.item_category,
      content_name: item.item_name,
      value: item.price,
      currency: 'USD',
    },
    uid
  );
}

export const getItems = (items: ProductLight[]) => {
  return items.map((i) => getItem(i));
};

export const getItem = (i: ProductLight) => {
  return {
    item_id: i.id,
    item_name: i.title,
    item_brand: i.brand || 'Google',
    item_category: i.category ?? undefined,
    item_category1: i.category1 ?? undefined,
    item_category2: i.category2 ?? undefined,
    price: i.price,
    quantity: 1,
  };
};
